import { useQuery } from '@apollo/client';
import { Box, Typography } from '@mui/material';
import { DataGridPremiumProps, GridEventListener, useGridApiRef } from '@mui/x-data-grid-premium';
import { useCallback, useEffect, useState } from 'react';

import GET_HEARINGS_PAGED from '../../../../api/apollo/query/GetHearingsPaged';
import { useAuth } from '../../../../api/auth';
import { CustomDataGrid } from '../../../../components/CustomDataGrid';
import TableToolbar from '../../../../components/Table/TableToolbar';
import { DefaultTableProps } from '../../../../components/Table/types';
import { Hearing } from '../../../../types';
import { useSummaryTableData } from '../../SummaryTableDataContext';
import styles from './Hearings.module.css';
import columns from './columns';

function NotesPanel({ row }: { row: Hearing }) {
    return (
        <Box sx={{ height: 300, p: 4 }}>
            <Typography variant="overline" className={styles.hearingsNotesTitle} gutterBottom>
                Hearing Notes
            </Typography>
            <Typography className={styles.notes} variant="body2">
                {row.notes}
            </Typography>
        </Box>
    );
}

const prepareHearings = (hearings: Hearing[]) => {
    return hearings.map((hearing) => {
        let stage = hearing.stage;

        if (stage) {
            stage = stage
                .split('_')
                .map((item, index) => (index === 0 ? item : item.toLowerCase()))
                .join(' ');
        }

        return {
            ...hearing,
            stage,
        };
    });
};

export const Hearings = ({ shrinkTitle, hideHeaders, showHeaders }: DefaultTableProps) => {
    const { user } = useAuth();
    const apiRef = useGridApiRef();
    const { tableData, setTableRows, appendTableRows } = useSummaryTableData();
    const [isFetchingAllPages, setIsFetchingAllPages] = useState(false);
    const rows = (tableData['hearings'] ?? []) as Hearing[];

    const PAGE_SIZE = 1000;

    const { loading, fetchMore } = useQuery(GET_HEARINGS_PAGED, {
        variables: { first: PAGE_SIZE },
        skip: tableData['hearings'] != null,
        onCompleted: ({ hearingsPaged }) => {
            const nodes = hearingsPaged.edges.map(({ node }) => node);
            setTableRows('hearings', prepareHearings(nodes));

            const fetchAllPages = async (hasNextPage: boolean, endCursor: string) => {
                if (!hasNextPage) return;
                const { data } = await fetchMore({
                    variables: { first: PAGE_SIZE, after: endCursor },
                });
                const moreNodes = data.hearingsPaged.edges.map(({ node }) => node);
                appendTableRows('hearings', prepareHearings(moreNodes));
                await fetchAllPages(data.hearingsPaged.pageInfo.hasNextPage, data.hearingsPaged.pageInfo.endCursor);
            };
            if (hearingsPaged.pageInfo.hasNextPage) {
                setIsFetchingAllPages(true);
                fetchAllPages(hearingsPaged.pageInfo.hasNextPage, hearingsPaged.pageInfo.endCursor).finally(() =>
                    setIsFetchingAllPages(false)
                );
            }
        },
    });

    useEffect(() => {
        const handleEvent: GridEventListener<'rowsScroll'> = (params) => {
            params.top > 5 ? hideHeaders() : showHeaders();
        };

        apiRef.current.subscribeEvent('rowsScroll', handleEvent);
    }, [apiRef, hideHeaders, showHeaders]);

    const getDetailPanelContent = useCallback<NonNullable<DataGridPremiumProps['getDetailPanelContent']>>(
        ({ row }) => <NotesPanel row={row} />,
        []
    );

    const getDetailPanelHeight = useCallback(() => 300, []);

    return (
        <CustomDataGrid
            apiRef={apiRef}
            getRowId={(row) => row.id}
            rowGroupingColumnMode="single"
            columns={columns}
            rows={rows}
            checkboxSelection={false}
            loading={loading || isFetchingAllPages}
            hideFooterSelectedRowCount
            components={{ Toolbar: TableToolbar }}
            componentsProps={{
                toolbar: {
                    title: 'Hearings',
                    showTitle: shrinkTitle,
                    csvOptions: { allColumns: true },
                    csvExportEnabled:
                        !loading && !isFetchingAllPages && user?.permissions?.map(({ codename }) => codename).includes('can_export_tables'),
                    tableApi: apiRef.current,
                    excelExportEnabled: false,
                },
            }}
            getDetailPanelHeight={getDetailPanelHeight}
            getDetailPanelContent={getDetailPanelContent}
            sx={{
                '& .MuiDataGrid-detailPanel': { bgcolor: '#F8F8F8' },
            }}
            initialState={{
                columns: {
                    columnVisibilityModel: {
                        notes: false,
                    },
                },
            }}
        />
    );
};

export default Hearings;
